import type { FunctionComponent } from "preact";
import type { MutableRef } from "preact/hooks";
import { FooterRecord } from "../../components/footer_record/footer_record.tsx";
import { preventDefaultCallback } from "../../components/utils.ts";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isLogined, login } from "../../store/user_info.ts";
import { useStore } from "@nanostores/preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { Icon } from "../../components/icon/icon.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import { normalizeAppRoute } from "../../router_utils.ts";
import { requestGet } from "../../utils/request.ts";
import Dialog, { useDialog } from "../../components/dialog/dialog.tsx";

export const LoginPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const isLogined = useStore($isLogined);
    const [_location, setLocation] = useLocation();
    const [isBusy, setIsBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errorText, setErrorText] = useState("");
    const inputUsernameRef: MutableRef<HTMLInputElement | null> = useRef(null);
    const inputPasswordRef: MutableRef<HTMLInputElement | null> = useRef(null);

    const [agreementConfig, setAgreementConfig] = useState<{ enabled: boolean; content: string }>({ enabled: false, content: "" });
    const [isAgreed, setIsAgreed] = useState(false);
    const agreementDialog = useDialog({
        title: t({ zh: "用户协议", en: "User Agreement", ar: "اتفاقية المستخدم" }),
        children: <div style={{ whiteSpace: "pre-wrap", maxHeight: "60vh", overflowY: "auto" }}>{agreementConfig.content}</div>,
        showCloseButton: true,
        clickOutsideCancel: true,
        footer: false,
    });

    const loginAction = useMemo(() => async () => {
        setErrorText("");
        setIsBusy(true);
        try {
            const un = inputUsernameRef.current?.value ?? "";
            const pwd = inputPasswordRef.current?.value ?? "";
            if (un.trim().length <= 0) {
                toast(t({ zh: "请输入用户名", en: "Please enter username", ar: "يرجى إدخال اسم المستخدم" }), "warn");
                inputUsernameRef.current?.focus();
                return;
            }
            if (pwd.length <= 0) {
                toast(t({ zh: "请输入密码", en: "Please enter password", ar: "يرجى إدخال كلمة المرور" }), "warn");
                inputPasswordRef.current?.focus();
                return;
            }
            if (agreementConfig.enabled && !isAgreed) {
                toast(t({ zh: "请先同意用户协议", en: "Please agree to the User Agreement", ar: "يرجى الموافقة على اتفاقية المستخدم" }), "warn");
                return;
            }
            await login(un, pwd);
        } catch (e) {
            const msg = (e as Error)?.message ?? t({ zh: "登录失败", en: "Login failed", ar: "فشل تسجيل الدخول" });
            setErrorText(msg);
            toast(msg, "error");
        } finally {
            setIsBusy(false);
        }
    }, [toast, t, setIsBusy, inputUsernameRef, inputPasswordRef, agreementConfig, isAgreed]);

    useEffect(() => {
        if (isLogined) {
            const goto = normalizeAppRoute(globalThis?.history?.state?.goto ?? globalThis?.history?.state?.next ?? "/");
            setLocation(goto, { replace: true, state: {} });
        }
    }, [isLogined]);

    useEffect(() => {
        inputUsernameRef.current?.focus();
    }, []);

    useEffect(() => {
        requestGet<{ data: { enabled: boolean; content: string } }>("/v2/system/user_agreement")
            .then((res) => {
                setAgreementConfig(res.data);
            })
            .catch((e) => {
                console.error("Failed to load user agreement", e);
            });
    }, []);

    return (
        <>
            <div class="farpov_login_screen">
                <div class="farpov_login_card" aria-busy={isBusy}>
                    <div class="farpov_login_brand">
                        <div class="farpov_brand_icon" aria-hidden="true">
                            <Icon name="camera" />
                            <div class="farpov_brand_ai_badge">
                                <span class="farpov_brand_ai_text">AI</span>
                            </div>
                        </div>
                        <div class="farpov_brand_text">
                            <div class="farpov_brand_title">FARPOV</div>
                            <div class="farpov_brand_subtitle">TIME-LAPSE SYSTEM</div>
                        </div>
                    </div>
                    <div class="farpov_login_heading">
                        <h2>{t({ zh: "登录", en: "Sign in", ar: "تسجيل الدخول" })}</h2>
                        <p>{t({ zh: "欢迎回来，请使用账号密码登录。", en: "Welcome back. Please sign in with your credentials.", ar: "مرحبًا بعودتك. يرجى تسجيل الدخول ببياناتك." })}</p>
                    </div>
                    {errorText ? <div class="farpov_login_error" role="alert">{errorText}</div> : null}
                    <form
                        onSubmit={async (e) => {
                            preventDefaultCallback(e);
                            await loginAction();
                        }}
                        class="farpov_login_form"
                    >
                        <label>
                            {t({ zh: "用户名", en: "Username", ar: "اسم المستخدم" })}
                            <input
                                type="text"
                                ref={inputUsernameRef}
                                autoComplete="username"
                                placeholder={t({ zh: "请输入用户名", en: "Enter username", ar: "أدخل اسم المستخدم" })}
                                disabled={isBusy}
                                aria-invalid={errorText ? "true" : "false"}
                            />
                        </label>
                        <label>
                            {t({ zh: "密码", en: "Password", ar: "كلمة المرور" })}
                            <div class="farpov_login_password_row">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    ref={inputPasswordRef}
                                    autoComplete="current-password"
                                    placeholder={t({ zh: "请输入密码", en: "Enter password", ar: "أدخل كلمة المرور" })}
                                    disabled={isBusy}
                                    aria-invalid={errorText ? "true" : "false"}
                                />
                                <button
                                    type="button"
                                    class="outline secondary farpov_login_toggle_password"
                                    disabled={isBusy}
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? t({ zh: "隐藏密码", en: "Hide password", ar: "إخفاء كلمة المرور" }) : t({ zh: "显示密码", en: "Show password", ar: "إظهار كلمة المرور" })}
                                >
                                    <Icon name={showPassword ? "eye-off" : "eye"} />
                                </button>
                            </div>
                        </label>
                        {agreementConfig.enabled ? (
                            <div style={{ marginBottom: "1rem" }}>
                                <label style={{ display: "flex", alignItems: "center", cursor: "pointer", width: "100%", margin: 0, justifyContent: "flex-start" }}>
                                    <input
                                        type="checkbox"
                                        checked={isAgreed}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsAgreed(!isAgreed);
                                        }}
                                        style={{ margin: 0, marginRight: "0.5rem", width: "auto", height: "auto", flexShrink: 0 }}
                                    />
                                    <span style={{ display: "inline-block", lineHeight: "1.2" }}>
                                        {t({ zh: "我已阅读并同意", en: "I have read and agree to ", ar: "لقد قرأت ووافقت على " })}
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                preventDefaultCallback(e);
                                                e.stopPropagation();
                                                agreementDialog.open();
                                            }}
                                            style={{ marginLeft: "0.2rem", display: "inline" }}
                                        >
                                            {t({ zh: "《用户协议》", en: "User Agreement", ar: "اتفاقية المستخدم" })}
                                        </a>
                                    </span>
                                </label>
                            </div>
                        ) : null}
                        <button
                            type="submit"
                            aria-busy={isBusy}
                            disabled={isBusy || (agreementConfig.enabled && !isAgreed)}
                            class="farpov_login_submit"
                            style={agreementConfig.enabled && !isAgreed ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        >
                            <span>{t({ zh: "登录", en: "Sign in", ar: "تسجيل الدخول" })}</span>
                            <Icon name="arrow-right" />
                        </button>
                    </form>
                </div>
                <FooterRecord class="farpov_login_footer" />
            </div>
        </>
    );
};
export default LoginPage;
